import { Checkbox, Heading, Modal } from "@indigo-components";
import { Segmented } from "antd";
import { useAtom } from "jotai";
import { FC, useState } from "react";
import { toolbarShortcutsRequireShiftAtom } from "~/atoms/toolbarShortcuts";
import { IS_MAC } from "~/utils/platform";
import "./KeyboardShortcutsModal.css";

interface KeyboardShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ShortcutCategory = "appointments" | "physicians" | "locations" | "general";

/** A single key combination (e.g., ["Ctrl", "Cmd", "S"]) */
type KeyCombo = string[];

interface Shortcut {
  description: string;
  /** Primary key combination for Mac, or array of alternatives */
  macKeys: KeyCombo | KeyCombo[];
  /** Primary key combination for Windows, or array of alternatives */
  winKeys: KeyCombo | KeyCombo[];
}

interface ShortcutSection {
  title: string;
  shortcuts: Shortcut[];
}

// Shortcut definitions organized by category with both Mac and Windows keys
const SHORTCUTS: Record<ShortcutCategory, ShortcutSection[]> = {
  appointments: [
    {
      title: "Floating Toolbar",
      shortcuts: [
        {
          description: "Ops to-dos",
          macKeys: ["Ctrl", "1"],
          winKeys: ["Alt", "1"],
        },
        {
          description: "Case summary / Key info",
          macKeys: ["Ctrl", "2"],
          winKeys: ["Alt", "2"],
        },
        {
          description: "Medical documents",
          macKeys: ["Ctrl", "3"],
          winKeys: ["Alt", "3"],
        },
        {
          description: "Impairment rating",
          macKeys: ["Ctrl", "4"],
          winKeys: ["Alt", "4"],
        },
        {
          description: "Validation",
          macKeys: ["Ctrl", "5"],
          winKeys: ["Alt", "5"],
        },
        {
          description: "Recap of opinions",
          macKeys: ["Ctrl", "6"],
          winKeys: ["Alt", "6"],
        },
        {
          description: "Indigo tips",
          macKeys: ["Ctrl", "7"],
          winKeys: ["Alt", "7"],
        },
        {
          description: "Case huddle",
          macKeys: ["Ctrl", "8"],
          winKeys: ["Alt", "8"],
        },
      ],
    },
    {
      title: "Edit & Save",
      shortcuts: [
        {
          description: "Edit current tab",
          macKeys: ["Ctrl", "Cmd", "E"],
          winKeys: ["Ctrl", "Alt", "E"],
        },
        {
          description: "Save changes",
          macKeys: ["Ctrl", "Cmd", "S"],
          winKeys: ["Ctrl", "Alt", "S"],
        },
      ],
    },
    {
      title: "Navigation",
      shortcuts: [
        {
          description: "Previous tab",
          macKeys: ["Ctrl", "Cmd", "←"],
          winKeys: ["Ctrl", "Alt", "←"],
        },
        {
          description: "Next tab",
          macKeys: ["Ctrl", "Cmd", "→"],
          winKeys: ["Ctrl", "Alt", "→"],
        },
      ],
    },
    {
      title: "Tab Shortcuts",
      shortcuts: [
        {
          description: "Hold to reveal appointment tab key hints",
          macKeys: ["Ctrl", "Cmd"],
          winKeys: ["Ctrl", "Alt"],
        },
        {
          description: "Appointment",
          macKeys: ["Ctrl", "Cmd", "A"],
          winKeys: ["Ctrl", "Alt", "A"],
        },
        {
          description: "Patient Info",
          macKeys: ["Ctrl", "Cmd", "P"],
          winKeys: ["Ctrl", "Alt", "P"],
        },
        {
          description: "Medical Record",
          macKeys: ["Ctrl", "Cmd", "M"],
          winKeys: ["Ctrl", "Alt", "M"],
        },
        {
          description: "History of Injury",
          macKeys: ["Ctrl", "Cmd", "H"],
          winKeys: ["Ctrl", "Alt", "H"],
        },
        {
          description: "Evaluation",
          macKeys: ["Ctrl", "Cmd", "V"],
          winKeys: ["Ctrl", "Alt", "V"],
        },
        {
          description: "Assessment",
          macKeys: ["Ctrl", "Cmd", "X"],
          winKeys: ["Ctrl", "Alt", "X"],
        },
        {
          description: "Upload",
          macKeys: ["Ctrl", "Cmd", "U"],
          winKeys: ["Ctrl", "Alt", "U"],
        },
        {
          description: "Report Review",
          macKeys: ["Ctrl", "Cmd", "R"],
          winKeys: ["Ctrl", "Alt", "R"],
        },
        {
          description: "Record Review",
          macKeys: ["Ctrl", "Cmd", "T"],
          winKeys: ["Ctrl", "Alt", "T"],
        },
        {
          description: "Billing",
          macKeys: ["Ctrl", "Cmd", "B"],
          winKeys: ["Ctrl", "Alt", "B"],
        },
      ],
    },
    {
      title: "Assessment",
      shortcuts: [
        {
          description: "Toggle dictation on/off",
          macKeys: ["Ctrl", "Option", "D"],
          winKeys: ["Ctrl", "Alt", "D"],
        },
        {
          // A double-tap, not a chord — same convention as the Indy hold row
          // below: the gesture only exists in the description.
          description:
            "Double-tap to start/stop dictation (while editing the assessment)",
          macKeys: ["Ctrl"],
          winKeys: ["Ctrl"],
        },
      ],
    },
    {
      title: "Record Review (Display Mode)",
      shortcuts: [
        {
          description: "Navigate to previous item",
          macKeys: ["↑"],
          winKeys: ["↑"],
        },
        {
          description: "Navigate to next item",
          macKeys: ["↓"],
          winKeys: ["↓"],
        },
        {
          description: "Expand/select item",
          macKeys: ["→"],
          winKeys: ["→"],
        },
        {
          description: "Collapse item",
          macKeys: ["←"],
          winKeys: ["←"],
        },
        {
          description: "Enter edit mode",
          macKeys: ["e"],
          winKeys: ["e"],
        },
        {
          description: "Clear focus",
          macKeys: ["Esc"],
          winKeys: ["Esc"],
        },
      ],
    },
    {
      title: "Record Review (Edit Mode) - Navigation",
      shortcuts: [
        {
          description: "Navigate to previous item",
          macKeys: ["Option", "↑"],
          winKeys: ["Alt", "↑"],
        },
        {
          description: "Navigate to next item",
          macKeys: ["Option", "↓"],
          winKeys: ["Alt", "↓"],
        },
        {
          description: "Open PDF",
          macKeys: ["Option", "→"],
          winKeys: ["Alt", "→"],
        },
        {
          description: "Focus first input in item",
          macKeys: ["Tab"],
          winKeys: ["Tab"],
        },
        {
          description:
            "Close suggestion details → exit input → item focus → clear",
          macKeys: ["Esc"],
          winKeys: ["Esc"],
        },
        {
          description: "Add new entry",
          macKeys: ["Ctrl", "Cmd", "N"],
          winKeys: ["Ctrl", "Alt", "N"],
        },
      ],
    },
    {
      title: "Record Review (Edit Mode) - Item Focused",
      shortcuts: [
        {
          description: "Focus content editor",
          macKeys: ["E"],
          winKeys: ["E"],
        },
        {
          description: "Star/unstar item",
          macKeys: [["F"], ["S"]],
          winKeys: [["F"], ["S"]],
        },
        {
          description: "Copy link to item",
          macKeys: ["L"],
          winKeys: ["L"],
        },
        {
          description: "Reset item changes",
          macKeys: ["R"],
          winKeys: ["R"],
        },
        {
          description: "Toggle entry deletion suggestion details",
          macKeys: ["?"],
          winKeys: ["?"],
        },
        {
          description: "Keep entry (reject suggested entry deletion)",
          macKeys: ["K"],
          winKeys: ["K"],
        },
        {
          description: "Delete item / accept suggested entry deletion",
          macKeys: ["D"],
          winKeys: ["D"],
        },
      ],
    },
    {
      title: "Record Review (Edit Mode) - Text Suggestions",
      shortcuts: [
        {
          description:
            "Focus suggestion actions (cursor inside highlighted text)",
          macKeys: ["Tab"],
          winKeys: ["Tab"],
        },
        {
          description: "Keep text (suggestion actions focused)",
          macKeys: ["K"],
          winKeys: ["K"],
        },
        {
          description: "Delete suggested text (suggestion actions focused)",
          macKeys: ["D"],
          winKeys: ["D"],
        },
        {
          description: "Return from suggestion actions to editor",
          macKeys: ["Esc"],
          winKeys: ["Esc"],
        },
      ],
    },
  ],
  physicians: [
    {
      title: "Edit & Save",
      shortcuts: [
        {
          description: "Edit current tab",
          macKeys: ["Ctrl", "Cmd", "E"],
          winKeys: ["Ctrl", "Alt", "E"],
        },
        {
          description: "Save changes",
          macKeys: ["Ctrl", "Cmd", "S"],
          winKeys: ["Ctrl", "Alt", "S"],
        },
      ],
    },
    {
      title: "Navigation",
      shortcuts: [
        {
          description: "Previous tab",
          macKeys: ["Ctrl", "Cmd", "←"],
          winKeys: ["Ctrl", "Alt", "←"],
        },
        {
          description: "Next tab",
          macKeys: ["Ctrl", "Cmd", "→"],
          winKeys: ["Ctrl", "Alt", "→"],
        },
        {
          description: "Previous physician",
          macKeys: ["Ctrl", "←"],
          winKeys: ["Ctrl", "←"],
        },
        {
          description: "Next physician",
          macKeys: ["Ctrl", "→"],
          winKeys: ["Ctrl", "→"],
        },
      ],
    },
    {
      title: "Tab Shortcuts",
      shortcuts: [
        {
          description: "Physician Info",
          macKeys: ["Ctrl", "Cmd", "I"],
          winKeys: ["Ctrl", "Alt", "I"],
        },
        {
          description: "Preferences",
          macKeys: ["Ctrl", "Cmd", "P"],
          winKeys: ["Ctrl", "Alt", "P"],
        },
        {
          description: "Availabilities",
          macKeys: ["Ctrl", "Cmd", "A"],
          winKeys: ["Ctrl", "Alt", "A"],
        },
        {
          description: "Feedback",
          macKeys: ["Ctrl", "Cmd", "F"],
          winKeys: ["Ctrl", "Alt", "F"],
        },
      ],
    },
  ],
  locations: [
    {
      title: "Edit & Save",
      shortcuts: [
        {
          description: "Edit current tab",
          macKeys: ["Ctrl", "Cmd", "E"],
          winKeys: ["Ctrl", "Alt", "E"],
        },
        {
          description: "Save changes",
          macKeys: ["Ctrl", "Cmd", "S"],
          winKeys: ["Ctrl", "Alt", "S"],
        },
      ],
    },
    {
      title: "Navigation",
      shortcuts: [
        {
          description: "Previous tab",
          macKeys: ["Ctrl", "Cmd", "←"],
          winKeys: ["Ctrl", "Alt", "←"],
        },
        {
          description: "Next tab",
          macKeys: ["Ctrl", "Cmd", "→"],
          winKeys: ["Ctrl", "Alt", "→"],
        },
        {
          description: "Previous location",
          macKeys: ["Ctrl", "←"],
          winKeys: ["Ctrl", "←"],
        },
        {
          description: "Next location",
          macKeys: ["Ctrl", "→"],
          winKeys: ["Ctrl", "→"],
        },
      ],
    },
    {
      title: "Tab Shortcuts",
      shortcuts: [
        {
          description: "Location Info",
          macKeys: ["Ctrl", "Cmd", "I"],
          winKeys: ["Ctrl", "Alt", "I"],
        },
      ],
    },
  ],
  general: [
    {
      title: "Global",
      shortcuts: [
        {
          description: "Focus search bar",
          macKeys: ["/"],
          winKeys: ["/"],
        },
        {
          description: "Open keyboard shortcuts",
          macKeys: ["Cmd", "Option", "/"],
          winKeys: ["Ctrl", "Alt", "/"],
        },
        {
          description:
            "Debug prompts for current appointment (uses highlighted text as output search)",
          macKeys: ["Ctrl", "Option", "L"],
          winKeys: ["Ctrl", "Alt", "L"],
        },
      ],
    },
    {
      title: "Indy",
      shortcuts: [
        {
          description: "Open or close the Indy chat panel",
          macKeys: ["Cmd", "K"],
          winKeys: ["Ctrl", "K"],
        },
        {
          // A hold, not a chord — the modal has no other way to say so, hence
          // the wording in the description.
          description:
            "Hold to dictate into the message box (release to stop; requires the message box to be focused)",
          macKeys: ["Ctrl"],
          winKeys: ["Ctrl"],
        },
        {
          // Same key as the row above, distinguished only by the gesture — so
          // the description has to carry "double-tap" against that one's "hold".
          description:
            "Double-tap to start/stop dictation in the message box (stays on until you stop it)",
          macKeys: ["Ctrl"],
          winKeys: ["Ctrl"],
        },
      ],
    },
  ],
};

const CATEGORY_LABELS: Record<ShortcutCategory, string> = {
  appointments: "Appointments",
  physicians: "Physicians",
  locations: "Locations",
  general: "General",
};

const KeyBadge: FC<{ keyName: string }> = ({ keyName }) => (
  <kbd className="keyboard-shortcut-key">{keyName}</kbd>
);

/** Renders a single key combination (e.g., Ctrl + Cmd + S) */
const KeyComboDisplay: FC<{ keys: KeyCombo }> = ({ keys }) => (
  <>
    {keys.map((key, index) => (
      <span key={index} className="keyboard-shortcut-key-group">
        <KeyBadge keyName={key} />
        {index < keys.length - 1 && (
          <span className="keyboard-shortcut-separator">+</span>
        )}
      </span>
    ))}
  </>
);

/** Check if keys is an array of key combos (alternatives) or a single combo */
const isMultipleKeyCombos = (keys: KeyCombo | KeyCombo[]): keys is KeyCombo[] =>
  keys.length > 0 && Array.isArray(keys[0]);

const ShortcutRow: FC<{ shortcut: Shortcut }> = ({ shortcut }) => {
  const keys = IS_MAC ? shortcut.macKeys : shortcut.winKeys;

  // Normalize to array of key combos
  const keyCombos: KeyCombo[] = isMultipleKeyCombos(keys) ? keys : [keys];

  return (
    <div className="keyboard-shortcut-row">
      <span className="keyboard-shortcut-description">
        {shortcut.description}
      </span>
      <div className="keyboard-shortcut-keys">
        {keyCombos.map((combo, comboIndex) => (
          <span key={comboIndex} className="keyboard-shortcut-combo">
            <KeyComboDisplay keys={combo} />
            {comboIndex < keyCombos.length - 1 && (
              <span className="keyboard-shortcut-or">/</span>
            )}
          </span>
        ))}
      </div>
    </div>
  );
};

const KeyboardShortcutsModal: FC<KeyboardShortcutsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [activeCategory, setActiveCategory] =
    useState<ShortcutCategory>("appointments");

  const [toolbarRequireShift, setToolbarRequireShift] = useAtom(
    toolbarShortcutsRequireShiftAtom,
  );
  const sections = SHORTCUTS[activeCategory];

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      centered
      width={560}
      className="keyboard-shortcuts-modal"
      title={<Heading level={1}>Keyboard Shortcuts</Heading>}
    >
      <div className="keyboard-shortcuts-content">
        <Segmented
          value={activeCategory}
          onChange={(value) => setActiveCategory(value as ShortcutCategory)}
          options={Object.entries(CATEGORY_LABELS).map(([value, label]) => ({
            value,
            label,
          }))}
          block
          className="keyboard-shortcuts-tabs"
        />

        <div className="keyboard-shortcuts-sections">
          {sections.map((section) => (
            <div key={section.title} className="keyboard-shortcut-section">
              <Heading level={3} className="keyboard-shortcut-section-title">
                {section.title}
              </Heading>
              {section.title === "Floating Toolbar" && (
                <div>
                  <Checkbox
                    checked={toolbarRequireShift}
                    onChange={(event) =>
                      setToolbarRequireShift(event.target.checked)
                    }
                    label="Require Shift for floating toolbar shortcuts"
                  />
                  <p className="mt-2 text-indigo-text-secondary">
                    Hold {IS_MAC ? "Control" : "Alt"}
                    {toolbarRequireShift ? " + Shift" : ""} to show toolbar
                    numbers. Only available tools respond. Enable Shift if a
                    system shortcut conflicts.
                  </p>
                </div>
              )}
              <div className="keyboard-shortcut-list">
                {section.shortcuts.map((shortcut, index) => (
                  <ShortcutRow
                    key={index}
                    shortcut={
                      section.title === "Floating Toolbar" &&
                      toolbarRequireShift
                        ? {
                            ...shortcut,
                            macKeys: ["Ctrl", "Shift", String(index + 1)],
                            winKeys: ["Alt", "Shift", String(index + 1)],
                          }
                        : shortcut
                    }
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};

export default KeyboardShortcutsModal;
